import { useState } from "react";
import { Card } from "../common/Card";
import { Badge } from "../common/Badge";
import { money } from "../../utils/format";
import { DataTable } from "../common/DataTable";
import { Button } from "../common/Button";
import { formatDateOnly } from "../../services/hiveActions";

/**
 * One vendor's whole history, in order (#254).
 *
 * The page used to show past orders, returns and empty jars as three
 * separate tables with a payment total elsewhere, so answering "why does
 * this vendor owe me a hundred pounds" meant reading three lists side by
 * side and reconstructing the sequence by hand. These events are one story:
 * honey out, some back, a payment, more out.
 *
 * The running balance comes from the server, computed over EVERY event
 * rather than the fifty each list shows, so the last line always agrees
 * with the Outstanding tile above it. Two numbers on one screen that are
 * meant to be the same have to be computed from the same thing.
 */

const TONE = {
  Delivery: "warning",
  Return: "neutral",
  Payment: "success",
  Empties: "neutral",
  Sale: "info",
};


/*
 * Money and jars, in one list, filtered (28 Aug).
 *
 * The merged list exists because "why does this vendor owe me a hundred
 * pounds" is answered by both kinds interleaved: honey out created the
 * debt, some came back, a payment landed. Splitting into two tables
 * would put that question back across two screens, which is exactly what
 * #254 removed.
 *
 * But money genuinely does lose space to jars, and the tell is the
 * Balance column: a Sale and an Empty-jar return both move zero, so they
 * print a balance that does not change and read like financial events
 * that did nothing.
 *
 * So: one list, ONE balance calculation, and a filter over it. The
 * balance is computed server-side across every event and never
 * recomputed here -- filtering the view must not change the arithmetic,
 * or the last line would stop agreeing with the outstanding figure above
 * it. Two numbers on one screen that are meant to match have to come
 * from the same place.
 */
const VIEWS = {
  all: { label: "All", kinds: null },
  // Events that move the balance. Empties and Sales are deliberately out:
  // the jars were charged for on delivery, so neither is money.
  money: { label: "Money", kinds: ["Delivery", "Return", "Payment"] },
  // Everything that moves glass, including the two that move no money --
  // which is the whole reason somebody wants this view.
  jars: { label: "Jars", kinds: ["Delivery", "Return", "Empties", "Sale"] },
};

export function VendorLedger({ transactions, onIssueReceipt, issuing }) {
  const [view, setView] = useState("all");
  const all = transactions || [];
  const kinds = VIEWS[view].kinds;
  const rows = kinds ? all.filter((t) => kinds.includes(t.kind)) : all;
  // Presence, not truthiness (#235): a row whose amount is genuinely 0 --
  // empty glass, a recorded sale -- still belongs in a money view.
  // Judged on ALL rows, not the filtered ones: choosing "Jars" must
  // not make the component think this is a money-hidden account.
  const showMoney = all.some((t) => "balance" in t);

  const columns = [
    { key: "date", header: "Date", render: (r) => formatDateOnly(r.date) },
    {
      key: "label",
      header: "What",
      render: (r) => (
        <div>
          <Badge tone={TONE[r.kind] || "neutral"}>{r.label}</Badge>{" "}
          {r.detail}
          {/* Which honey it was (V063). Beside the quantity rather than in
              the note, because "12 x 1 lb Wildflower" is one fact. */}
          {r.varietal_name ? ` · ${r.varietal_name}` : ""}
          {r.note ? (
            <div style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>
              {r.note}
            </div>
          ) : null}
        </div>
      ),
    },
  ];

  if (showMoney) {
    columns.push(
      // Zero amounts are shown as a dash rather than $0.00: empty glass and
      // a recorded sale genuinely move no money, and printing $0.00 invites
      // the reader to wonder whether something failed.
      { key: "amount", header: "Amount", render: (r) => (Number(r.amount) ? money(r.amount) : "—") },
      { key: "balance", header: "Balance", render: (r) => money(r.balance) },
    );
    if (onIssueReceipt) {
      // Optional, always. Most sales will never want one, so it stays a
      // quiet action on the row rather than a step in the flow (#193) --
      // Business is sold as confidence, never as enforcement.
      columns.push({
        key: "receipt",
        header: "",
        // Deliveries AND payments (reported 27 Aug). The receipt
        // endpoint has always taken a payment_id and issueFor has always
        // handled "Payment" -- only this row check stopped it being
        // reachable. And a payment receipt is the one a VENDOR asks for:
        // proof they settled up, which is exactly the document somebody
        // wants a copy of.
        render: (r) =>
          r.kind === "Delivery" || r.kind === "Payment" ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={issuing === r.source_id}
              onClick={(e) => { e.stopPropagation(); onIssueReceipt(r.source_id, r.kind); }}
            >
              {issuing === r.source_id ? "…" : "Receipt"}
            </Button>
          ) : null,
      });
    }
  } else {
    // The informal tier's entire ledger: did they pay for that drop-off.
    // It is a fact rather than an amount, so it survives where money does
    // not -- the same rule that keeps the last-payment date visible.
    columns.push({
      key: "paid",
      header: "Paid",
      render: (r) => (r.kind === "Delivery" ? (r.paid ? "Yes" : "Not yet") : ""),
    });
  }

  return (
    <Card title="Transactions" className="section-spacing">
      <p style={{ marginTop: 0, color: "var(--color-text-muted)" }}>Newest first.</p>
      {all.length > 0 && (
        <div style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-3)" }}>
          {Object.entries(VIEWS).map(([key, v]) => (
            <Button
              key={key}
              size="sm"
              variant={view === key ? "primary" : "secondary"}
              onClick={() => setView(key)}
            >
              {v.label}
            </Button>
          ))}
        </div>
      )}

      <DataTable
        rows={rows}
        rowKey="id"
        columns={columns}
        emptyTitle="Nothing yet"
        emptyDescription="Deliveries, returns and payments will appear here in order."
      />
    </Card>
  );
}
